import {
  Component,
  signal,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { ItemListResponse, User, UserListResponse } from '@models';
import { NotificationService, UserService } from '@services';
import { UserFormComponent } from '../form/form.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { httpResource } from '@angular/common/http';
import { environment } from '@environment';
import { MatListModule, MatListOption } from '@angular/material/list';
import { ListComponent } from '@components/list/list.component';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
  selector: 'app-user-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    FormsModule,

    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatIconModule,
    MatSortModule,
    MatListModule,

    ListComponent,
  ],
})
export class UserListComponent {
  userService = inject(UserService);
  notificationService = inject(NotificationService);
  dialog = inject(MatDialog);
  router = inject(Router);
  route = inject(ActivatedRoute);

  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || 10);
  q = signal(this.route.snapshot.queryParams['q'] as string || '');

  users = httpResource(() => `${environment.komainuHost}/account/?q=${this.q()}&skip=${this.page() * this.pageSize()}&take=${this.pageSize()}&count`, {
    parse: (response: unknown) => {
      const r = response as UserListResponse;
      return {
        items: r?.accounts || [],
        count: r?.count || 0,
      } as ItemListResponse<User>;
    },
  });

  constructor() {
    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || 10);
      this.q.set(params['q'] as string || '');
    });
  }

  create() {
    const dialogRef = this.dialog.open(UserFormComponent, {
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((email: string | null) => {
      if (!email) {
        return;
      }

      this.userService.createUser(email).subscribe({
        next: () => {
          this.notificationService.success('User created successfully.');
          this.users.reload();
        },
        error: (err) => {
          if (err?.status === 409) {
            this.notificationService.error('User already exists.');
          } else {
            console.error(err);
            this.notificationService.error('Failed to create user.');
          }
        },
      });
    });
  }

  recover(user: User) {
    if (user.status !== 'inactive') {
      this.userService.triggerAccountRecovery(user.email).subscribe({
        next: () => {
          this.notificationService.success('Pushed recovery token to user.');
          this.users.reload();
        },
        error: (err) => {
          if (err?.status === 404) {
            this.notificationService.error('User does not exist.');
          } else {
            console.error(err);
            this.notificationService.error('Failed to push recovery token.');
          }
        },
      });
    }
  }

  toggleStatus(user: User) {
    let activate = false;
    if (user.status === 'inactive') {
      activate = true;
    }

    this.userService.updateUserStatus(user.id, activate).subscribe({
      next: () => {
        if (activate) {
          this.notificationService.success('Account enabled successfully.');
        } else {
          this.notificationService.success('Account disabled successfully.');
        }
        this.users.reload();
      },
      error: (err) => {
        if (err?.status === 404) {
          this.notificationService.error('User does not exist.');
        } else {
          console.error(err);
          this.notificationService.error('Failed to update user status.');
        }
      },
    });
  }

  isDirectoryUser(user: User): boolean {
    return !!user && user.email?.endsWith('@npolar.no')
  }

  getUser(option: MatListOption): User | null {
    return this.users.value()?.items.find(u => u.id === option.value) || null;
  }

  onSearchEvent(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value || undefined,
        page: undefined,
      },
      replaceUrl: true
    });
  }

  onPageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === 10 ? undefined : event.pageSize,
      },
      replaceUrl: true
    });
  }
}
